/**
 * Controls the wholesale tee buy-box:
 *  - clicking a volume tier sets the quantity
 *  - typing a quantity highlights the matching tier
 *  - the top unit price, the "Save %" badge, the "<range> selected" label and
 *    the add-to-cart label ("Add N to cart · total") stay in sync
 *
 * All related elements (price, quantity input, add-to-cart) live in the same
 * product section, so the controller scopes its queries to the closest
 * `.shopify-section`.
 */
import { ThemeEvents } from '@theme/events';

class ProductUnitPriceTiers extends HTMLElement {
  #abortController;
  #scope;
  #base = 0;
  #tiers = [];

  connectedCallback() {
    // A fresh controller per connect — the element can be detached/re-attached
    // by section morphing, and reusing an aborted signal would silently drop
    // the listeners.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#scope = this.closest('.shopify-section') ?? document;

    // The tier ladder is merchant-editable: there can be one tier or ten, so
    // everything below is derived from whatever tiles the Liquid rendered and
    // nothing is assumed about their number or their prices.
    this.#tiers = Array.from(this.querySelectorAll('[data-tier]'))
      .map((el) => ({
        el,
        min: Number(el.dataset.min) || 1,
        unit: Number(el.dataset.unit),
        save: Number(el.dataset.save) || 0,
      }))
      .filter((tier) => Number.isFinite(tier.unit) && tier.unit > 0);

    // `data-base-price` is the price of the lowest-minimum tier. If it is
    // missing, fall back to the dearest tile rather than to a hardcoded amount.
    const declaredBase = Number(this.dataset.basePrice);
    this.#base =
      Number.isFinite(declaredBase) && declaredBase > 0
        ? declaredBase
        : this.#tiers.reduce((max, tier) => Math.max(max, tier.unit), 0);

    // A tile with no `data-save` gets its saving worked out against the base,
    // the same integer-floored percentage the Liquid uses.
    for (const tier of this.#tiers) {
      if (tier.save > 0 || this.#base <= 0 || tier.unit >= this.#base) continue;
      tier.save = Math.floor(((this.#base - tier.unit) * 100) / this.#base);
    }

    this.addEventListener('click', this.#handleClick, { signal });

    const qtyInput = this.#qtyInput;
    if (qtyInput) {
      qtyInput.addEventListener('change', this.#sync, { signal });
      qtyInput.addEventListener('input', this.#sync, { signal });
    }

    // The quantity stepper's +/- buttons update the input value and dispatch a
    // bubbling QuantitySelectorUpdateEvent instead of a native input/change
    // event, so listen for that too.
    this.#scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync, { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get #qtyInput() {
    return this.#scope?.querySelector('product-form-component input[name="quantity"]') ?? null;
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-tier]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const tierMin = Number(button.dataset.min) || 1;
    const qtyInput = this.#qtyInput;
    if (qtyInput instanceof HTMLInputElement) {
      qtyInput.value = String(this.#snapToInput(tierMin, qtyInput));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#sync();
  };

  /**
   * Snaps a tier minimum onto the quantity input's own min / step / max, so
   * picking a tier can never leave a quantity the product does not allow: a
   * product sold in packs of 12 goes to 36 for the "25+" tier, not to 25.
   */
  #snapToInput(quantity, input) {
    const min = Number(input?.min) > 0 ? Number(input.min) : 1;
    const step = Number(input?.step) > 0 ? Number(input.step) : 1;
    const max = Number(input?.max) > 0 ? Number(input.max) : Infinity;

    let snapped = min + Math.ceil(Math.max(quantity - min, 0) / step) * step;
    if (snapped > max) {
      // Can't reach the tier: stop at the largest allowed quantity instead.
      snapped = min + Math.floor((max - min) / step) * step;
    }

    return Math.max(Math.round(snapped), 1);
  }

  #currentQty() {
    const value = parseInt(this.#qtyInput?.value ?? '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * The tier with the largest minimum the quantity reaches. Tiles are rendered
   * in setting order, which a merchant can scramble, so the winner is picked on
   * `min` rather than on position. A quantity below every minimum falls back to
   * the lowest-minimum tier, which is the base price.
   */
  #tierIndexForQty(qty) {
    let index = -1;
    let bestMin = -Infinity;

    this.#tiers.forEach((tier, i) => {
      if (qty >= tier.min && tier.min > bestMin) {
        bestMin = tier.min;
        index = i;
      }
    });

    if (index >= 0) return index;

    let lowest = 0;
    this.#tiers.forEach((tier, i) => {
      if (tier.min < (this.#tiers[lowest]?.min ?? Infinity)) lowest = i;
    });
    return lowest;
  }

  #money(amount) {
    return '$' + amount.toFixed(2);
  }

  #sync = () => {
    if (!this.#tiers.length) return;

    const qty = this.#currentQty();
    const index = this.#tierIndexForQty(qty);
    const tier = this.#tiers[index];

    // Tier card active states
    this.#tiers.forEach((entry, i) => {
      const isActive = i === index;
      entry.el.classList.toggle('product-unit-price-tiers__item--active', isActive);
      entry.el.setAttribute('aria-pressed', String(isActive));
    });

    // "<range> selected" label
    const range = tier.el.querySelector('.product-unit-price-tiers__range')?.textContent?.trim();
    const selectedLabel = this.querySelector('[data-tier-selected-label]');
    if (selectedLabel && range) selectedLabel.textContent = `${range} selected`;

    // Top unit price + saving badge
    const priceUnit = this.#scope?.querySelector('[data-tee-price-unit]');
    if (priceUnit) priceUnit.textContent = this.#money(tier.unit);

    const savePctEl = this.#scope?.querySelector('[data-tee-price-savepct]');
    if (savePctEl) savePctEl.textContent = String(tier.save);

    const saving = this.#scope?.querySelector('[data-tee-price-saving]');
    if (saving instanceof HTMLElement) saving.hidden = tier.save <= 0;

    // Add-to-cart label: "Add N to cart · total"
    const atcContent = this.#scope?.querySelector('.add-to-cart-text__content');
    if (atcContent) {
      const target = atcContent.querySelector('span span') ?? atcContent;
      target.textContent = `Add ${qty} to cart · ${this.#money(tier.unit * qty)}`;
    }
  };
}

if (!customElements.get('product-unit-price-tiers')) {
  customElements.define('product-unit-price-tiers', ProductUnitPriceTiers);
}
